import { DEFAULT_TIMEZONE } from '@shared/constants/system/timezone';
import { calendarDayIn } from '@shared/utils/core/timezone';
import { DateTime, IANAZone } from 'luxon';

/**
 * Curium Lite trial (CUR-2693). Staff pick the trial's last calendar day; the org stores the last
 * instant of that day in its report timezone as `Org.trialEndsAt`. One module defines the day
 * boundary for the server lockout, the top-bar countdown and the System Console, so they agree
 * across DST transitions.
 *
 * Every `timeZone` argument is the org's `reportTimezone` as stored: unset or unusable falls back
 * to `DEFAULT_TIMEZONE`, the column default.
 */

/** A new Light org's trial ends this many days after the day it is created. */
export const LITE_TRIAL_DEFAULT_DAYS = 14;

/** What a user of an org whose trial has ended sees, at sign-in and on the expiry screen. */
export const TRIAL_EXPIRED_MESSAGE =
    "Your trial has expired. Email your account manager or support@getcurium.com if you'd like to upgrade.";

function trialZone(timeZone: string | null | undefined): string {
    return timeZone && IANAZone.isValidZone(timeZone) ? timeZone : DEFAULT_TIMEZONE;
}

/** The last instant (23:59:59.999) of `day` (`YYYY-MM-DD`) in the zone: the stored `trialEndsAt`. */
export function trialEndsAtForDay(day: string, timeZone: string | null | undefined): Date {
    const start = DateTime.fromISO(day, { zone: trialZone(timeZone) });
    if (!start.isValid) throw new RangeError(`Not a calendar day: ${day}`);
    return start.endOf('day').toJSDate();
}

/** The trial's last calendar day (`YYYY-MM-DD`) in the zone. */
export function trialEndDayOf(trialEndsAt: Date, timeZone: string | null | undefined): string {
    return calendarDayIn(trialEndsAt, trialZone(timeZone));
}

/** Today in the zone plus `LITE_TRIAL_DEFAULT_DAYS`, as a calendar day. */
export function defaultTrialEndDay(now: Date, timeZone: string | null | undefined): string {
    const today = DateTime.fromJSDate(now).setZone(trialZone(timeZone)).startOf('day');
    const day = today.plus({ days: LITE_TRIAL_DEFAULT_DAYS }).toISODate();
    if (!day) throw new RangeError('Could not resolve the default trial end day');
    return day;
}

/** The trial has ended once `now` is past its last instant. */
export function isTrialEnded(trialEndsAt: Date, now: Date): boolean {
    return now.getTime() > trialEndsAt.getTime();
}

/**
 * Whole calendar days from today to the trial's last day, both taken in the zone: 0 on the last
 * day, negative once the trial has ended.
 */
export function trialDaysLeft(trialEndsAt: Date, now: Date, timeZone: string | null | undefined): number {
    const zone = trialZone(timeZone);
    const today = DateTime.fromJSDate(now).setZone(zone).startOf('day');
    const lastDay = DateTime.fromJSDate(trialEndsAt).setZone(zone).startOf('day');
    return Math.round(lastDay.diff(today, 'days').days);
}

/** The top-bar countdown for a trial that has not ended. */
export function formatTrialCountdown(daysLeft: number): string {
    if (daysLeft <= 0) return 'Your trial ends today';
    if (daysLeft === 1) return '1 day left in your trial';
    return `${String(daysLeft)} days left in your trial`;
}
